import React, { useMemo } from 'react';
import { DashboardDataRaw, DateRange } from '../../types/Dashboard';
import { StatCard, Tone } from '../ui';
import { formatCurrency, formatNumber, formatSigned } from './dashboardUi';
import {
  activesAtDate,
  exclusive8080AutoRevenue,
  lastDayOfMonth,
  manualOverrideForMonth,
  today,
} from './dashboardHelpers';

interface Props {
  data: DashboardDataRaw;
  range: DateRange;
}

const EXCLUSIVE_PLAN = 'Exclusive 8080';
const CHURN_STATUSES = new Set(['Inactivo', 'Baja', 'Cancelado']);

const KPICards: React.FC<Props> = ({ data, range }) => {
  const isCurrentEnd = range.to === today();
  const rangeLabel = isCurrentEnd ? 'hoy' : 'al cierre del rango';

  const { revenue, totalActivos, totalAltas, totalBajas } = useMemo(() => {
    const activesEnd = activesAtDate(data.premium, range.to);
    const totalActivos = Object.values(activesEnd).reduce((s, n) => s + n, 0);

    // Revenue: precio * activos para planes no-exclusivos + manual-o-auto para el exclusivo
    let revenue = 0;
    for (const [plan, count] of Object.entries(activesEnd)) {
      if (plan === EXCLUSIVE_PLAN) continue;
      revenue += count * (data.precios[plan] ?? 0);
    }
    const snapshotEoM = lastDayOfMonth(range.to);
    const exclusiveOverride = manualOverrideForMonth(
      data.manualRevenueHistory[EXCLUSIVE_PLAN] ?? [],
      snapshotEoM
    );
    if (exclusiveOverride) {
      revenue += exclusiveOverride.monthly_amount;
    } else {
      revenue += exclusive8080AutoRevenue(data.premium, snapshotEoM, EXCLUSIVE_PLAN).revenue;
    }

    // Altas: effectiveFrom en [range.from, range.to] y plan no nulo
    let totalAltas = 0;
    for (const row of data.premium) {
      const from = row.effectiveFrom.slice(0, 10);
      if (from >= range.from && from <= range.to && row.plan !== null) totalAltas++;
    }

    // Bajas: effectiveTo en [range.from, range.to] y status de churn
    let totalBajas = 0;
    for (const row of data.premium) {
      if (!CHURN_STATUSES.has(row.status) || row.effectiveTo === null) continue;
      const to = row.effectiveTo.slice(0, 10);
      if (to >= range.from && to <= range.to) totalBajas++;
    }

    return { revenue, totalActivos, totalAltas, totalBajas };
  }, [data, range]);

  const neto = totalAltas - totalBajas;

  const cards: { label: string; value: string; sub: string; tone: Tone; title?: string }[] = [
    {
      label: `Ingreso mensual ${rangeLabel}`,
      value: formatCurrency(revenue),
      sub: 'Cuota de todos los planes premium activos',
      tone: 'default',
      title: 'Suma de cuota × activos por plan. El Exclusive 8080 entra con su revenue manual o, si no hay, con el auto-calculado por edad.',
    },
    {
      label: `Suscriptores premium ${rangeLabel}`,
      value: formatNumber(totalActivos),
      sub: 'Vigentes en algún plan de pago',
      tone: 'info',
    },
    {
      label: 'Altas en el rango',
      value: formatSigned(totalAltas),
      sub: 'Nuevas suscripciones premium',
      tone: 'positive',
    },
    {
      label: 'Bajas en el rango',
      value: formatSigned(-totalBajas),
      sub: `Cancelaciones · neto ${formatSigned(neto)}`,
      tone: 'negative',
      title: 'Neto = altas menos bajas dentro del rango elegido.',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <StatCard
          key={card.label}
          label={card.label}
          value={card.value}
          sub={card.sub}
          tone={card.tone}
          title={card.title}
        />
      ))}
    </div>
  );
};

export default KPICards;
